import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Collapse,
  IconButton,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const useCases = [
  {
    id: 'tax',
    emoji: '📋',
    title: 'Tax Season Ready',
    color: '#fbbf24',
    description: 'Export all "Maybe Charitable Donation" or other tagged items for your analysis. Generate both CSV and PDF reports with totals.',
  },
  {
    id: 'hsa',
    emoji: '🏥',
    title: 'HSA/FSA Tracking',
    color: '#06b6d4',
    description: 'Filter by "Maybe HSA/FSA" tags to get possible reimbursable HSA/FSA tagged expenses in a report.',
  },
  {
    id: 'custom',
    emoji: '📊',
    title: 'Custom Analysis',
    color: '#8b5cf6',
    description: 'Create your own tags and generate focused reports for any spending category you want to track.',
  },
  {
    id: 'expense',
    emoji: '📑',
    title: 'Expense Report',
    color: '#22c55e',
    isNew: true,
    description: 'Hand-pick specific receipts and generate PDFs with receipt images attached. Perfect for work reimbursements.',
  },
];

const STORAGE_KEY = 'exportCenter_useCasesCollapsed';

function UseCasesSection({ onSelectMode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isCollapsed.toString());
  }, [isCollapsed]);

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.05) 50%, rgba(6, 182, 212, 0.08) 100%)',
        border: '1px solid rgba(34, 197, 94, 0.2)',
        overflow: 'hidden',
        mb: 3,
        position: 'relative',
        zIndex: 1
      }}
    >
      {/* Header - Always visible */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.02)'
          }
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#22c55e',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '1rem'
        }}>
          <Box sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: '#22c55e',
            display: 'inline-block'
          }} />
          💡 Use Case Ideas
        </Typography>
        <IconButton size="small" sx={{ color: '#22c55e' }}>
          {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>

      {/* Collapsible content */}
      <Collapse in={!isCollapsed}>
        <Box sx={{ px: 3, pb: 3 }}>
          <Grid container spacing={2}>
            {useCases.map((useCase) => (
              <Grid item xs={12} sm={6} md={3} key={useCase.id}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1.5, 
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      transform: 'translateY(-2px)',
                      borderColor: useCase.color,
                    }
                  }}
                  onClick={() => {
                    if (useCase.id === 'expense') {
                      onSelectMode('individual');
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: useCase.color }}>
                      {useCase.emoji} {useCase.title}
                    </Typography>
                    {useCase.isNew && (
                      <Box sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: 'rgba(34, 197, 94, 0.2)',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        color: '#22c55e',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        New
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4, flex: 1 }}>
                    {useCase.description}
                  </Typography>
                  {useCase.id === 'expense' && (
                    <Button
                      size="small"
                      sx={{ 
                        mt: 1.5, 
                        color: useCase.color,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        p: 0,
                        justifyContent: 'flex-start',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Start Building →
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default UseCasesSection;

